import * as Sentry from "@sentry/react";
import {useAtom, useSetAtom} from "jotai";
import React, {useState} from "react";
import PhoneInputWithCountrySelect, { Country } from "react-phone-number-input";
import {useNavigate} from "react-router-dom";
import {getPhone} from "../../../lib/getPhone.ts";
import Header from "../Header/Header.tsx";
import {verify} from "../../../lib/api.ts";
import {OtpSentAtom, PhoneAtom, useDirectLinkState} from "../lib/state.ts";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";

import styles from './Phone.module.css';

const Phone: React.FC = () => {
    const navigate = useNavigate();
    const [directLink] = useDirectLinkState();
    const [phone, setPhone] = useAtom(PhoneAtom);
    const setOtpSent = useSetAtom(OtpSentAtom);
    const [number, setNumber] = useState<string | undefined>(
        phone?.formatInternational() ?? undefined
    );
    const [country, setCountry] = useState<Country>('US');
    const { executeRecaptcha } = useGoogleReCaptcha();

    const onSubmit = (event: React.SyntheticEvent) => {  // es-lint is finicky about promises and event handlers
        event.preventDefault();
        void onNext();
    }

    const onNext = async () => {
        try {
            const parsed = getPhone(number ?? '', country);
            if (parsed) {
                setPhone(parsed);
                const recaptchaToken = executeRecaptcha
                    ? await executeRecaptcha('send_code')
                    : undefined;
                await verify(parsed.formatInternational()!, undefined, recaptchaToken);
                setOtpSent(true);
                navigate('../auth');
            }
        } catch (e: unknown) {
            console.error(e);
            Sentry.captureException(e);
        }
    }

    const onPhoneCountryChange = (nextCountry?: Country) => {
        setCountry(nextCountry ?? 'US');
    }

    const onPhoneNumberChange = (value?: string) => {
        setNumber(value);
    }

    return (
        <>
            <Header title="Verify your phone number" step={0} show_image={false} />

            <div className="content">

                {directLink.unauthorized_error && <h3>Please provide the phone number from which this reservation was created.</h3>}

                <form className="section" onSubmit={onSubmit}>
                    <div className={styles.field}>
                        <label>Phone number</label>
                        <PhoneInputWithCountrySelect
                            placeholder="Enter phone number"
                            value={number}
                            country={country ?? 'US'}
                            defaultCountry="US"
                            onChange={onPhoneNumberChange}
                            onCountryChange={onPhoneCountryChange}
                        />
                    </div>

                    <input type="submit" className="nextButton" disabled={!number} value={`Next`} />
                </form>
            </div>
        </>
    )
}

export default Phone
